import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import { getVapiCallStatus, getCallPayload } from '../../services/vapi.service';
import { CircularProgress } from '@material-ui/core';
import Transcript from '../../components/CallManager/components/Transcript';

const styles = theme => ({
  root: {
    padding: 24,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 24,
  },
  title: {
    color: '#1e5180',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    flex: 1,
    minHeight: 0,
  },
  topSection: {
    display: 'flex',
    gap: 16,
    marginBottom: 16,
    minHeight: 0,
    '& > div': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
  },
  outerContainer: {
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  innerContainer: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sectionHeader: {
    padding: '16px 24px',
    borderBottom: '1px solid #e0e0e0',
    textAlign: 'left',
  },
  sectionHeaderText: {
    fontSize: '20px',
    color: '#1e5180',
    fontWeight: 500,
    textAlign: 'left',
  },
  callInfoContent: {
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  infoGrid: {
    marginBottom: 24,
  },
  infoLabel: {
    color: '#666',
    fontWeight: 500,
  },
  infoValue: {
    color: '#333',
  },
  status: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 16,
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontSize: 14,
  },
  specialInstructions: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    '& .label': {
      marginBottom: 8,
      color: '#666',
      fontWeight: 500,
    },
    '& textarea': {
      flex: 1,
      minHeight: 80,
      maxHeight: 120,
      padding: 12,
      border: '1px solid #ddd',
      borderRadius: 8,
      resize: 'none',
      fontSize: 14,
      fontFamily: 'inherit',
      '&::placeholder': {
        color: '#999',
      },
    },
  },
  refreshButton: {
    backgroundColor: '#3a87ad',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2a6d8d',
    },
  },
  endCallButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    marginLeft: 16,
    '&:hover': {
      backgroundColor: '#c82333',
    },
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  backButton: {
    minWidth: 40,
    height: 40,
    padding: 8,
    backgroundColor: '#f5f5f5',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
});

export const CallMonitor = withStyles(styles)(({ classes }) => {
  const { orderLineId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const vapiCallId = new URLSearchParams(location.search).get('vapiCallId');
  const [callStatus, setCallStatus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [callDetails, setCallDetails] = useState(null);
  const [callDetailsLoading, setCallDetailsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCallDetails = async () => {
    if (!orderLineId) return;

    setCallDetailsLoading(true);
    try {
      const data = await getCallPayload(orderLineId);
      setCallDetails(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching call details:', err);
      setError('Failed to load call details');
    }
    setCallDetailsLoading(false);
  };

  const fetchTranscript = async () => {
    if (!vapiCallId) return;

    setLoading(true);
    try {
      const status = await getVapiCallStatus(vapiCallId);
      setCallStatus(status || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching transcript:', err);
      setError('Failed to load transcript');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCallDetails();
  }, [orderLineId]);

  useEffect(() => {
    fetchTranscript();
  }, [vapiCallId]);

  const handleBack = () => {
    history.goBack();
  };

  if (error) {
    return (
      <div className={classes.root}>
        <Typography color="error" align="center">
          {error}
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.titleSection}>
          <Button className={classes.backButton} style={{ marginRight: 16 }} variant="contained" onClick={handleBack}>
            <ArrowBackIcon />
            Back
          </Button>
          <Typography variant="h4" className={classes.title}>
            Call Details: {orderLineId}
          </Typography>
        </div>
        <div>
          <Button
            variant="contained"
            className={classes.refreshButton}
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchTranscript();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button variant="contained" className={classes.endCallButton} onClick={() => {}}>
            END CALL
          </Button>
        </div>
      </div>

      <div className={classes.content}>
        <div className={classes.topSection}>
          <div>
            <div style={{ marginRight: 8 }} className={classes.outerContainer}>
              <Paper className={classes.innerContainer} elevation={0}>
                <div className={classes.sectionHeader}>
                  <Typography className={classes.sectionHeaderText}>Call Information</Typography>
                </div>
                {callDetailsLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress />
                  </div>
                ) : (
                  <div className={classes.callInfoContent}>
                    <Grid container spacing={2} className={classes.infoGrid}>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Order ID:</Typography>
                        <Typography className={classes.infoValue}>
                          {callDetails?.assistantOverrides?.variableValues?.orderLineId}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Customer:</Typography>
                        <Typography className={classes.infoValue}>
                          {callDetails?.assistantOverrides?.variableValues?.insuredName}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Phone:</Typography>
                        <Typography className={classes.infoValue}>
                          {callDetails?.assistantOverrides?.variableValues?.insuredPhone}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Status:</Typography>
                        <Typography className={classes.status}>{callDetails?.status || 'Queued'}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Start Time:</Typography>
                        <Typography className={classes.infoValue}>
                          {new Date(callStatus?.startedAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Duration:</Typography>
                        <Typography className={classes.infoValue}>
                          {callDetails?.assistantOverrides?.variableValues?.duration || '0:00'}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Assistant:</Typography>
                        <Typography className={classes.infoValue}>
                          {callDetails?.assistantOverrides?.variableValues?.assistant || 'Assistant #1'}
                        </Typography>
                      </Grid>
                    </Grid>
                    <div className={classes.specialInstructions}>
                      <Typography className="label">Special Instructions:</Typography>
                      <textarea
                        placeholder="Add special instructions here..."
                        value={callDetails?.assistantOverrides?.variableValues?.specialInstructions || ''}
                        onChange={e =>
                          setCallDetails(prev => ({
                            ...prev,
                            specialInstructions: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                )}
              </Paper>
            </div>
          </div>
          <div>
            <div style={{ marginLeft: 8 }} className={classes.outerContainer}>
              <Paper className={classes.innerContainer} elevation={0}>
                <Transcript loading={loading} transcript={callStatus?.messages} />
              </Paper>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
